import React, { useEffect, useState, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { fetchApiDataIfNeeded, callApi, callFileApi, manageApiData } from '../../../actions/api'
import { apiResourceEndpoint } from '../../../constants/routes'
import RoadTestStep from './step'
import '../../../assets/scss/refactor/_road-test.scss'

const selectRoadTests = state => state.roadTests
const selectStepResults = state => state.roadTestStepResults
const selectStepMedia = state => state.roadTestStepMedia
const selectInstallationId = (state, installationId) => installationId

// Stop window monitoring after this long without a successful poll.
const POLL_GIVE_UP_MS = 60 * 1000

const makeRoadTest = createSelector(
    [selectRoadTests, selectInstallationId],
    (roadTests, installationId) => roadTests.find(rt => rt.installationId === Number(installationId))
)

const mapStateToProps = (state, ownProps) => ({
    roadTest: makeRoadTest(state, ownProps.installationId),
    stepResults: selectStepResults(state),
    stepMedia: selectStepMedia(state)
})

// A step is unlocked when it's the first step, or the previous step has passed / been skipped.
const isUnlocked = (steps, resultByKey, index) => {
    if (index === 0) {
        return true
    }
    const prevStatus = resultByKey[steps[index - 1].key]?._computed?.statusValue
    return prevStatus === 'PASSED' || prevStatus === 'SKIPPED'
}

const RoadTestPanel = ({ dispatch, installationId, roadTest, stepResults, stepMedia }) => {
    const [openKey, setOpenKey] = useState(null)
    // Key of the most recent completing action. We advance only once that step actually resolves to
    // PASSED/SKIPPED on the server — never assuming the action passed.
    const [pendingAdvance, setPendingAdvance] = useState(null)
    // The window step currently being polled (live signal monitoring), if any.
    const [monitoringKey, setMonitoringKey] = useState(null)
    // Bumped on every reset. A poll only lands in the store if no reset happened while it was in
    // flight — clearing monitoringKey stops the interval but can't cancel a request already sent.
    const monitorGeneration = useRef(0)
    // True while a window poll is outstanding. The provider fetch can outlast the 5s interval, so a
    // tick is skipped rather than stacking overlapping polls that race to save.
    const pollInFlight = useRef(false)
    // When the last window poll succeeded. Network blips are common, so a failed poll alone doesn't
    // stop monitoring — only going a full minute without a good response does (that also catches a
    // request that hangs, since a hung poll blocks the ones after it).
    const lastPollOk = useRef(0)

    const fetchRoadTest = useCallback(() => {
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/installations', 'VIEW', installationId, 'road-test'), {}, true))
    }, [dispatch, installationId])

    useEffect(() => {
        fetchRoadTest()
    }, [fetchRoadTest])

    const imei = roadTest?.imei
    const steps = roadTest?.definition?.steps ?? []
    const resultByKey = {}
    if (roadTest) {
        stepResults
            .filter(sr => sr.roadTestId === roadTest.id)
            .forEach(sr => {
                resultByKey[sr.stepKey] = sr
            })
    }

    // The first step that's unlocked and not yet resolved — auto-expanded so the runner opens on the
    // next actionable step and advances as the frontier moves.
    const firstActionableStep = steps.find((s, i) => {
        const status = resultByKey[s.key]?._computed?.statusValue
        return isUnlocked(steps, resultByKey, i) && status !== 'PASSED' && status !== 'SKIPPED'
    })
    const firstActionableKey = firstActionableStep?.key ?? null

    useEffect(() => {
        if (firstActionableKey) {
            setOpenKey(firstActionableKey)
        }
    }, [firstActionableKey])

    // Advance to the next step by order once the acted-on step resolves to PASSED/SKIPPED. This
    // covers re-submitting an already-passed step (the frontier effect above only fires when the
    // first-unresolved step changes) without assuming any action passed.
    useEffect(() => {
        if (!pendingAdvance) {
            return
        }
        const status = resultByKey[pendingAdvance]?._computed?.statusValue
        if (status === 'PASSED' || status === 'SKIPPED') {
            const idx = steps.findIndex(s => s.key === pendingAdvance)
            if (idx >= 0 && idx + 1 < steps.length) {
                setOpenKey(steps[idx + 1].key)
            }
            setPendingAdvance(null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepResults, pendingAdvance])

    // While a window step is being monitored, poll its window endpoint every 5s. Each poll fetches
    // the batch since the stored cursor, accumulates the series, and re-evaluates server-side; the
    // response updates the step in the store, so the graph fills in left-to-right.
    useEffect(() => {
        if (!monitoringKey || !roadTest || !imei) {
            return undefined
        }
        const rtId = roadTest.id
        lastPollOk.current = Date.now()
        // Set once this effect gives up. The alert blocks, and a tick queued behind it can run before
        // React tears the interval down — without this it would alert a second time.
        let stopped = false
        const poll = () => {
            if (stopped) {
                return
            }
            if (Date.now() - lastPollOk.current > POLL_GIVE_UP_MS) {
                stopped = true
                setMonitoringKey(null)
                window.alert('No response from signal monitoring for over a minute, so it has been stopped. Check the connection and IMEI, then start monitoring again.')
                return
            }
            if (pollInFlight.current) {
                return
            }
            pollInFlight.current = true
            const generation = monitorGeneration.current
            dispatch(callApi(apiResourceEndpoint('admin/road-tests', 'VIEW', rtId, `steps/${monitoringKey}/window`), {}, null, true))
                .then(data => {
                    if (!data) {
                        return
                    }
                    lastPollOk.current = Date.now()
                    if (generation === monitorGeneration.current) {
                        dispatch(manageApiData(data))
                    }
                })
                .finally(() => {
                    pollInFlight.current = false
                })
        }
        poll()
        const interval = setInterval(poll, 5000)
        return () => clearInterval(interval)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [monitoringKey, roadTest?.id, imei, dispatch])

    // Stop polling once the monitored step passes (and queue the advance), or once it's been marked
    // FAILED/SKIPPED by hand — the server refuses to monitor those until the step is reset.
    useEffect(() => {
        const statusValue = monitoringKey && resultByKey[monitoringKey]?._computed?.statusValue
        if (statusValue === 'PASSED') {
            setPendingAdvance(monitoringKey)
            setMonitoringKey(null)
        } else if (statusValue === 'FAILED' || statusValue === 'SKIPPED') {
            setMonitoringKey(null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepResults, monitoringKey])

    if (!roadTest) {
        return <div className="content-box"><p>Loading road test…</p></div>
    }

    // Every completing action ends here: refetch, and mark the step so the effect advances it once
    // the server says it passed/skipped.
    const completeStep = (stepKey) => {
        fetchRoadTest()
        setPendingAdvance(stepKey)
    }

    const startMonitoring = (stepKey) => setMonitoringKey(stepKey)
    const stopMonitoring = () => setMonitoringKey(null)

    // Throw away everything collected for the step so its test starts over from the next monitoring
    // start — for a run that went wrong, and between the cycles of a step that has to be performed
    // more than once. Polling is paused across the reset, and bumping the generation drops any window
    // response still in flight so it can't restore the discarded series (the server also refuses a
    // poll's save if the row changed under it).
    const resetMonitoring = (stepKey) => {
        if (!window.confirm('Discard the data collected for this step and start its test over?')) {
            return
        }
        // Only the step being reset is paused; resetting another step leaves its monitoring alone.
        // The generation is bumped either way — the reset step may still have a poll in flight from
        // before it was stopped; another monitored step just loses one tick, which the next fills.
        const wasMonitoring = monitoringKey === stepKey
        monitorGeneration.current = monitorGeneration.current + 1
        if (wasMonitoring) {
            setMonitoringKey(null)
        }
        // callApi resolves 'SUCCESS' on success and swallows failures (resolving undefined), so only
        // an explicit success resumes monitoring. On failure the old run is still live server-side;
        // leave monitoring stopped and say so rather than quietly carry on as if it was reset.
        dispatch(callApi(apiResourceEndpoint('admin/road-tests', 'UPDATE', roadTest.id, `steps/${stepKey}/window/reset`), {}, null))
            .then(outcome => {
                if (outcome !== 'SUCCESS') {
                    window.alert(wasMonitoring
                        ? 'The step could not be reset. Monitoring has been stopped; please try again.'
                        : 'The step could not be reset; please try again.')
                    return
                }
                setPendingAdvance(null)
                if (wasMonitoring) {
                    setMonitoringKey(stepKey)
                }
            })
    }

    // A manual status change on the monitored step ends its monitoring: stop the interval and drop
    // any window response still in flight so it can't paint over the technician's decision.
    const endMonitoringFor = (stepKey) => {
        if (monitoringKey === stepKey) {
            monitorGeneration.current = monitorGeneration.current + 1
            setMonitoringKey(null)
        }
    }

    // SHSForm submits: dispatch only (the formId drives the form status); completion runs in successFX.
    const submitStepUpdate = (stepKey, payload, formId) => {
        endMonitoringFor(stepKey)
        dispatch(callApi(apiResourceEndpoint('admin/road-tests', 'UPDATE', roadTest.id, `steps/${stepKey}`), payload, formId))
    }

    const submitStepPhotos = (stepKey, formData, formId) => {
        dispatch(callFileApi(apiResourceEndpoint('admin/road-tests', 'UPDATE', roadTest.id, `steps/${stepKey}/photos`), formData, formId))
    }

    // Signal steps advance via plain buttons (no form): dispatch, then complete.
    const advanceStep = (stepKey, status) => {
        endMonitoringFor(stepKey)
        dispatch(callApi(apiResourceEndpoint('admin/road-tests', 'UPDATE', roadTest.id, `steps/${stepKey}`), { status }, null))
            .then(() => completeStep(stepKey))
    }

    // Read + evaluate the latest signal for a static step server-side.
    const readSignal = (stepKey) => {
        if (!imei) {
            return
        }
        dispatch(callApi(apiResourceEndpoint('admin/road-tests', 'VIEW', roadTest.id, `steps/${stepKey}/signal`), {}, null))
            .then(() => completeStep(stepKey))
    }

    // Manual navigation cancels any pending advance so we don't yank the user off a step they opened.
    const toggleStep = (stepKey) => {
        setPendingAdvance(null)
        setOpenKey(openKey === stepKey ? null : stepKey)
    }

    return (
        <div className="road-test-runner">
            <div className="road-test-header">
                <div className="rt-imei">
                    <label>Signal IMEI</label>
                    <span>{imei || 'No IMEI submitted for this installation'}</span>
                </div>
                <span className="rt-status">{roadTest._computed?.statusDisplay}</span>
            </div>
            {steps.map((step, index) => {
                const result = resultByKey[step.key]
                return (
                    <RoadTestStep
                        key={step.key}
                        step={step}
                        result={result}
                        media={stepMedia.filter(m => m.roadTestStepResultId === result?.id)}
                        unlocked={isUnlocked(steps, resultByKey, index)}
                        expanded={openKey === step.key}
                        onToggle={() => toggleStep(step.key)}
                        submitStepUpdate={submitStepUpdate}
                        submitStepPhotos={submitStepPhotos}
                        advanceStep={advanceStep}
                        readSignal={readSignal}
                        onComplete={completeStep}
                        startMonitoring={startMonitoring}
                        stopMonitoring={stopMonitoring}
                        resetMonitoring={resetMonitoring}
                        monitoring={monitoringKey === step.key}
                        imei={imei}
                    />
                )
            })}
        </div>
    )
}

export default connect(mapStateToProps)(RoadTestPanel)
